import * as React from 'react';
import { Device, Playlist, Profile } from '@raydiant/api-client-js';
import config from '../../config';
import { t } from 'i18next';
import { embedsPlaylists } from '../../utilities/embedsPlaylists';
import { isAmazonSignageStick } from './deviceManagementRows';

const liteSupportedAppIds = [
  config.flyersAppId,
  config.picturesAppId,
  config.youtubeAppId,
  config.cloverAppId,
];

export interface ContentWarning {
  severity: 'warning' | 'error';
  message: React.ReactNode;
  priority: number;
}

export type ContentWarningMap = Record<
  | 'noContent'
  | 'unsupportedContent'
  | 'multiplePresentations'
  | 'scheduledContent',
  ContentWarning
>;

export const BrightSignWarnings: ContentWarningMap = {
  noContent: {
    severity: 'error',
    message: 'BrightSign only supports Pictures, Flyers, YouTube and Clover',
    priority: 100,
  },
  unsupportedContent: {
    severity: 'warning',
    message: 'BrightSign only supports Pictures, Flyers, YouTube and Clover',
    priority: 30,
  },
  multiplePresentations: {
    severity: 'warning',
    message: 'BrightSign does not support more than one presentation',
    priority: 20,
  },
  scheduledContent: {
    severity: 'warning',
    message: 'BrightSign does not support scheduled content',
    priority: 10,
  },
};

export const warningsByPriority: Record<string, ContentWarningMap> = {
  lite: {
    noContent: {
      severity: 'error',
      message:
        'ScreenRay Lite only supports Pictures, Flyers, YouTube and Clover',
      priority: 100,
    },
    unsupportedContent: {
      severity: 'warning',
      message:
        'ScreenRay Lite only supports Pictures, Flyers, YouTube and Clover',
      priority: 30,
    },
    multiplePresentations: {
      severity: 'warning',
      message: 'ScreenRay Lite does not support more than one presentation',
      priority: 20,
    },
    scheduledContent: {
      severity: 'warning',
      message: 'ScreenRay Lite does not support scheduled content',
      priority: 10,
    },
  },
  Brightsign: BrightSignWarnings,
  BrightSign: BrightSignWarnings,
  Poly: {
    noContent: {
      severity: 'error',
      message: `This free version of ${t(
        'companyName',
      )} only supports Pictures, Flyers and YouTube`,
      priority: 100,
    },
    unsupportedContent: {
      severity: 'warning',
      message: `This free version of ${t(
        'companyName',
      )} only supports Pictures, Flyers and YouTube`,
      priority: 30,
    },
    multiplePresentations: {
      severity: 'warning',
      message: `This free version of ${t(
        'companyName',
      )} does not support more than one presentation`,
      priority: 20,
    },
    scheduledContent: {
      severity: 'warning',
      message: `This free version of ${t(
        'companyName',
      )} does not support scheduled content`,
      priority: 10,
    },
  },
};

// A Signage Stick plays every app except those that embed playlists, which it
// skips. The copy names Multizone, the only one of them in real use.
export const stickWarnings: Pick<
  ContentWarningMap,
  'noContent' | 'unsupportedContent'
> = {
  noContent: {
    severity: 'error',
    message: 'Amazon Signage Stick does not support Multizone presentations',
    priority: 100,
  },
  unsupportedContent: {
    severity: 'warning',
    message: 'Amazon Signage Stick does not support Multizone presentations',
    priority: 30,
  },
};

// Reads the playlist's top-level items only: the playlist response gives a
// nested playlist no items, so a live nested playlist counts as something that
// may play.
export function getStickContentWarning(
  playlist: Playlist,
): ContentWarning | null {
  let skippedCount = 0;
  let mayPlayCount = 0;

  for (const item of playlist.items) {
    if (item.presentation) {
      if (item.presentation.resource.deletedAt !== null) {
        continue;
      }
      if (embedsPlaylists(item.presentation)) {
        skippedCount += 1;
      } else {
        mayPlayCount += 1;
      }
    } else if (item.playlist) {
      if (item.playlist.resource.deletedAt) {
        continue;
      }
      // Scheduled content that has ended. A recurring nested playlist plays
      // again after its first end, so its own recurrence rule decides.
      if (
        item.playlist.endDatetime &&
        !item.playlist.recurrenceRule &&
        +new Date(item.playlist.endDatetime) < +new Date()
      ) {
        continue;
      }
      mayPlayCount += 1;
    }
  }

  if (skippedCount === 0) {
    return null;
  }
  return mayPlayCount === 0
    ? stickWarnings.noContent
    : stickWarnings.unsupportedContent;
}

const setWarningByPriority = (
  current: ContentWarning | null,
  next: ContentWarning,
) => {
  // Set warning if there's no current warning or current warning has a lower priority.
  if (!current || current.priority < next.priority) {
    return next;
  }
  return current;
};

export default function getDeviceContentWarning(
  device: Device,
  playlist: Playlist,
  profile: Profile,
): ContentWarning | null {
  if (isAmazonSignageStick(device)) {
    return getStickContentWarning(playlist);
  }

  let contentWarning: ContentWarning | null = null;
  const isSocDevice = device.deviceType === 'SoC';

  const isUpgradedPolyDevice =
    profile.polyServiceLevel === 'full' && device.model === 'Poly';

  if (
    device.isLite &&
    !isSocDevice &&
    device.deviceType !== 'RaydiantLite' &&
    device.model !== 'SecondScreen' &&
    device.model !== 'Amazon Signage Stick' &&
    !isUpgradedPolyDevice
  ) {
    let supportedAppCount = 0;
    let unsupportedAppCount = 0;

    const modelWarnings =
      warningsByPriority[device.model] || warningsByPriority['lite'];

    for (const item of playlist.items) {
      if (item.presentation) {
        // Skip deleted presentations
        if (item.presentation.resource.deletedAt !== null) {
          continue;
        }

        // Check for unsupported content.
        const isSupportedContent = liteSupportedAppIds.includes(
          // TODO: Use the new application name field (not currently returned by the API)
          // instead of app ids.
          item.presentation.applicationId,
        );

        if (isSupportedContent) {
          supportedAppCount += 1;
        } else {
          unsupportedAppCount += 1;
        }

        // Check for multiple presentations.
        if (supportedAppCount > 1) {
          contentWarning = setWarningByPriority(
            contentWarning,
            modelWarnings.multiplePresentations,
          );
        }
      } else if (item.playlist) {
        // Skip deleted playlists
        if (item.playlist.resource.deletedAt) {
          continue;
        }

        // Skip non-scheduled playlists
        if (!item.playlist.endDatetime) {
          continue;
        }

        // Skip scheduled content that has ended.
        const endDate = new Date(item.playlist.endDatetime);
        const now = new Date();
        if (!playlist.recurrenceRule && +endDate < +now) {
          continue;
        }

        contentWarning = setWarningByPriority(
          contentWarning,
          modelWarnings.scheduledContent,
        );
      }
    }

    // Override warning with error if there's no supported content in playlist.
    if (supportedAppCount === 0) {
      contentWarning = setWarningByPriority(
        contentWarning,
        modelWarnings.noContent,
      );
    }

    // Show warning for playlist with unsupported content
    if (unsupportedAppCount !== 0) {
      contentWarning = setWarningByPriority(
        contentWarning,
        modelWarnings.unsupportedContent,
      );
    }
  }

  return contentWarning;
}
